// New / Edit document dialog v3.
//
// Layout order (as directed):
//   1. Type of Doc — clickable tag chips (Proposal / Company Policy /
//      Guidelines / Other + custom text field when Other selected).
//   2. Important toggle (On/Off).
//   3. Title (single input).
//   4. Description — textarea with insertion buttons above (Insert Link,
//      Contact @mention, Doc reference, Custom placeholder).
//   5. Compact "Applicable for all employees" toggle + individual user picker.
//   6. Multi-attachment builder (files + URLs, add as many as needed).
//   7. Optional Schedule + Optional Expiry with action selector.
//   8. Reviewers (MA-only) — pick employees who must review & Good-to-Go.
//
// Insertion buttons insert markdown-style tokens at the caret:
//   [Link Title](https://…)                     → clickable pill
//   @[User Name]                                → user chip
//   #[Doc Title]                                → doc chip
//   {{placeholder}}                             → dynamic placeholder pill

import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";
import UserPickerDialog from "../UserPickerDialog";
import {
  FileText, Link as LinkIcon, X, Folder, UserCheck, Users, Globe2,
  ShieldAlert, CalendarClock, Clock, Archive, RefreshCcw, AtSign, Hash,
  Braces, Sparkles, Eye,
} from "lucide-react";
import { TAG_META, TAG_ORDER, fmtSize, toLocalInput, attachmentFlavor } from "./docConstants";

function emptyForm() {
  return {
    title: "",
    description: "",
    tag: "proposal",
    tag_custom: "",
    is_important: false,
    target_all: true,
    target_user_ids: [],
    scheduled_for: "",
    end_at: "",
    expiry_action: "archive",
    attachments: [],
    reviewer_ids: [],
  };
}

const TAG_ICONS = {
  proposal: Sparkles,
  guidelines: FileText,
  company_policy: ShieldAlert,
  other: Braces,
};

/** Insert `snippet` at the caret position of `ref.current`, return the new value. */
function insertAtCaret(ref, current, snippet) {
  const el = ref.current;
  if (!el) return current + snippet;
  const start = el.selectionStart ?? current.length;
  const end = el.selectionEnd ?? current.length;
  const next = current.slice(0, start) + snippet + current.slice(end);
  requestAnimationFrame(() => {
    if (!el) return;
    el.focus();
    const pos = start + snippet.length;
    el.setSelectionRange(pos, pos);
  });
  return next;
}

// --- Uniform section wrapper -----------------------------------------------
// Every field in the form is wrapped in an identical framed card with a
// numbered step chip + label. Optional `right` renders inline actions on
// the header (used by the Description block for insertion buttons).
function SectionCard({ step, title, Icon, required, count, right, children }) {
  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
      <header className="flex items-center gap-2 px-4 pt-3.5 pb-2 border-b border-slate-100 dark:border-slate-800">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-sky-600 text-white text-[10.5px] font-black tracking-wider">
          {step}
        </span>
        {Icon && <Icon size={13} className="text-slate-500 dark:text-slate-400" />}
        <h3 className="text-[12px] uppercase tracking-wider font-bold text-slate-800 dark:text-slate-100">
          {title}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
          {Number.isFinite(count) && <span className="ml-1 text-slate-400 dark:text-slate-500 font-normal">· {count}</span>}
        </h3>
        {right && <div className="ml-auto flex items-center gap-1">{right}</div>}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

// --- Small link/contact/doc pickers -----------------------------------------

function InsertLinkPop({ open, onClose, onInsert }) {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  useEffect(() => { if (open) { setUrl(""); setLabel(""); } }, [open]);
  if (!open) return null;
  return (
    <div className="absolute z-50 mt-1 right-0 w-[320px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-3 space-y-2">
      <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700">Insert Link</div>
      <Input placeholder="Label (e.g. Google Drive)" value={label} onChange={(e) => setLabel(e.target.value)} />
      <Input placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} />
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="h-8 text-[11px]">Cancel</Button>
        <Button
          onClick={() => {
            const u = url.trim();
            if (!u || !(u.startsWith("http://") || u.startsWith("https://"))) {
              toast.error("URL must start with http:// or https://"); return;
            }
            onInsert(`[${(label || u).trim()}](${u})`);
            onClose();
          }}
          className="h-8 text-[11px] bg-sky-600 hover:bg-sky-700 text-white"
        ><LinkIcon size={11} className="mr-1" /> Insert</Button>
      </div>
    </div>
  );
}

function InsertContactPop({ open, onClose, users, onInsert }) {
  const [q, setQ] = useState("");
  useEffect(() => { if (open) setQ(""); }, [open]);
  if (!open) return null;
  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [u.name, u.email, u.role].some((v) => String(v || "").toLowerCase().includes(s));
  }).slice(0, 20);
  return (
    <div className="absolute z-50 mt-1 right-0 w-[320px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-3 space-y-2">
      <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700">Insert Contact</div>
      <Input autoFocus placeholder="Search users…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="max-h-56 overflow-y-auto rounded border border-slate-100 dark:border-slate-800 divide-y">
        {filtered.map((u) => (
          <button
            key={u.id}
            onClick={() => { onInsert(`@[${u.name}]`); onClose(); }}
            className="w-full text-left px-2.5 py-1.5 hover:bg-indigo-50"
          >
            <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-200 truncate">{u.name}</div>
            <div className="text-[10.5px] text-slate-500 dark:text-slate-400 truncate">{u.email} · {u.role}</div>
          </button>
        ))}
        {filtered.length === 0 && <div className="text-center text-[11px] text-slate-500 dark:text-slate-400 py-3">No match</div>}
      </div>
    </div>
  );
}

function InsertDocPop({ open, onClose, onInsert }) {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open) return;
    setQ("");
    api.get("/documents?bucket=current").then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
  }, [open]);
  if (!open) return null;
  const filtered = docs.filter((d) => {
    if (!q) return true;
    return String(d.title || "").toLowerCase().includes(q.toLowerCase());
  }).slice(0, 20);
  return (
    <div className="absolute z-50 mt-1 right-0 w-[320px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-3 space-y-2">
      <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700">Insert Doc Reference</div>
      <Input autoFocus placeholder="Search published docs…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="max-h-56 overflow-y-auto rounded border border-slate-100 dark:border-slate-800 divide-y">
        {filtered.map((d) => (
          <button
            key={d.id}
            onClick={() => { onInsert(`#[${d.title}]`); onClose(); }}
            className="w-full text-left px-2.5 py-1.5 hover:bg-indigo-50"
          >
            <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-200 truncate">{d.title}</div>
            <div className="text-[10.5px] text-slate-500 dark:text-slate-400 truncate">{TAG_META[d.tag]?.label || d.tag}</div>
          </button>
        ))}
        {filtered.length === 0 && <div className="text-center text-[11px] text-slate-500 dark:text-slate-400 py-3">No match</div>}
      </div>
    </div>
  );
}

function InsertPlaceholderPop({ open, onClose, onInsert }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  if (!open) return null;
  return (
    <div className="absolute z-50 mt-1 right-0 w-[300px] rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-3 space-y-2">
      <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700">Insert Placeholder</div>
      <Input autoFocus placeholder="e.g. client_name, deal_size" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="h-8 text-[11px]">Cancel</Button>
        <Button onClick={() => { const v = text.trim(); if (v) { onInsert(`{{${v}}}`); onClose(); } }} className="h-8 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white">
          Insert
        </Button>
      </div>
    </div>
  );
}

// --- Description live preview -----------------------------------------------

function DescriptionPreview({ text }) {
  if (!text) return <span className="text-slate-400 dark:text-slate-500 italic">Nothing to preview yet — start typing above.</span>;
  // Parse tokens: [label](url), @[name], #[title], {{placeholder}}
  const nodes = [];
  const pattern = /(\[[^\]]+\]\([^)]+\))|(@\[[^\]]+\])|(#\[[^\]]+\])|(\{\{[^}]+\}\})/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) nodes.push(<span key={key++}>{text.slice(last, match.index)}</span>);
    const raw = match[0];
    if (raw.startsWith("[")) {
      const m = raw.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (m) nodes.push(<a key={key++} href={m[2]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-300 px-1.5 py-0.5 text-[11px] font-semibold hover:bg-sky-200"><LinkIcon className="w-2.5 h-2.5" />{m[1]}</a>);
    } else if (raw.startsWith("@")) {
      const inner = raw.slice(2, -1);
      nodes.push(<span key={key++} className="inline-flex items-center gap-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300 px-1.5 py-0.5 text-[11px] font-semibold"><AtSign className="w-2.5 h-2.5" />{inner}</span>);
    } else if (raw.startsWith("#")) {
      const inner = raw.slice(2, -1);
      nodes.push(<span key={key++} className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-300 px-1.5 py-0.5 text-[11px] font-semibold"><Hash className="w-2.5 h-2.5" />{inner}</span>);
    } else if (raw.startsWith("{{")) {
      const inner = raw.slice(2, -2);
      nodes.push(<span key={key++} className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 text-[11px] font-mono">{`{{${inner}}}`}</span>);
    }
    last = match.index + raw.length;
  }
  if (last < text.length) nodes.push(<span key={key++}>{text.slice(last)}</span>);
  return <div className="whitespace-pre-wrap leading-relaxed text-[13px] text-slate-700">{nodes}</div>;
}

// --- Main dialog ------------------------------------------------------------

export default function NewDocDialog({ open, onClose, onSaved, users, editing, resubmitMode = false, currentUserRole = "" }) {
  const [form, setForm] = useState(emptyForm());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reviewerPickerOpen, setReviewerPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pop, setPop] = useState(""); // 'link' | 'contact' | 'doc' | 'ph'
  const [pendingUrl, setPendingUrl] = useState("");
  const fileRef = useRef(null);
  const descRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const isEditing = !!editing;
  const isMaster = currentUserRole === "master_admin";

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const atts = (editing.attachments || []).map((a) => (
        a.type === "file"
          ? { kind: "existing_file", type: "file", index: a.index, filename: a.filename, content_type: a.content_type, size: a.size, gridfs_id: a.gridfs_id, description: a.description || "" }
          : { kind: "url", url: a.external_url, hostname: a.hostname, description: a.description || "" }
      ));
      setForm({
        title: editing.title || "",
        description: editing.description || "",
        tag: editing.tag || "proposal",
        tag_custom: editing.tag_custom || "",
        is_important: !!editing.is_important,
        target_all: !!(editing.target_all ?? editing.applicable_to_all),
        target_user_ids: editing.target_user_ids || editing.allowed_user_ids || [],
        scheduled_for: toLocalInput(editing.scheduled_for),
        end_at: toLocalInput(editing.end_at),
        expiry_action: editing.expiry_action || "archive",
        attachments: atts,
        reviewer_ids: (editing.reviewers || []).map((r) => r.user_id).filter(Boolean),
      });
    } else {
      setForm(emptyForm());
    }
    setPendingUrl(""); setPop(""); setShowPreview(false);
  }, [open, editing]);

  const insertToDescription = (snippet) => {
    setForm((s) => ({ ...s, description: insertAtCaret(descRef, s.description, snippet) }));
  };

  const addFiles = (fileList) => {
    if (!fileList) return;
    const items = Array.from(fileList).map((f) => ({ kind: "file", file: f, description: "" }));
    setForm((s) => ({ ...s, attachments: [...s.attachments, ...items] }));
    if (fileRef.current) fileRef.current.value = "";
  };
  const addUrl = () => {
    const u = pendingUrl.trim();
    if (!u) return;
    if (!(u.startsWith("http://") || u.startsWith("https://"))) { toast.error("URL must start with http:// or https://"); return; }
    setForm((s) => ({ ...s, attachments: [...s.attachments, { kind: "url", url: u, description: "" }] }));
    setPendingUrl("");
  };
  const removeAttachment = (idx) => setForm((s) => ({ ...s, attachments: s.attachments.filter((_, i) => i !== idx) }));
  const updateAttachmentDescription = (idx, description) => {
    setForm((s) => ({ ...s, attachments: s.attachments.map((a, i) => (i === idx ? { ...a, description } : a)) }));
  };

  const submit = async (mode) => {
    const asDraft = mode === "draft";
    const isResubmit = mode === "resubmit";

    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.tag === "other" && !form.tag_custom.trim()) { toast.error("Please write your custom tag"); return; }
    if (!asDraft && !form.target_all && form.target_user_ids.length === 0) { toast.error("Audience is empty — pick users or 'All Employees'"); return; }
    if (!asDraft && form.attachments.length === 0) { toast.error("Add at least one file or link"); return; }

    setBusy(true);
    try {
      if (isEditing) {
        const newFiles = form.attachments.filter((a) => a.kind === "file" && a.file);
        let staged = [];
        if (newFiles.length) {
          const sfd = new FormData();
          newFiles.forEach((a) => sfd.append("files", a.file, a.file.name));
          const { data } = await api.post("/documents/staged-upload", sfd, { headers: { "Content-Type": "multipart/form-data" } });
          staged = data;
        }
        let si = 0;
        const kept = form.attachments.map((a) => {
          if (a.kind === "existing_file") return { type: "file", index: a.index, description: a.description || "" };
          if (a.kind === "url") return { type: "url", external_url: a.url, description: a.description || "" };
          return { type: "file", staged_id: staged[si++]?.staged_id, description: a.description || "" };
        });
        const body = {
          title: form.title,
          description: form.description,
          tag: form.tag,
          tag_custom: form.tag_custom,
          is_important: form.is_important,
          target_all: form.target_all,
          target_user_ids: form.target_user_ids,
          scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null,
          end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
          expiry_action: form.expiry_action,
          attachments: kept,
        };
        if (asDraft) body.save_as_draft = "true";
        else if (isResubmit) body.resubmit = "true";
        else body.publish_now = "true";
        await api.patch(`/documents/${editing.id}`, body);
        // MA-only: sync reviewers on edit too
        if (isMaster) {
          try { await api.post(`/documents/${editing.id}/reviewers/assign`, { reviewer_ids: form.reviewer_ids || [] }); }
          catch { /* non-fatal */ }
        }
        toast.success(asDraft ? "Saved as draft" : (isResubmit ? "Resubmitted for approval" : "Document updated"));
      } else {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("tag", form.tag);
        if (form.tag === "other") fd.append("tag_custom", form.tag_custom);
        fd.append("is_important", form.is_important ? "true" : "false");
        fd.append("target_all", form.target_all ? "true" : "false");
        fd.append("target_user_ids", (form.target_user_ids || []).join(","));
        if (form.scheduled_for) fd.append("scheduled_for", new Date(form.scheduled_for).toISOString());
        if (form.end_at) fd.append("end_at", new Date(form.end_at).toISOString());
        fd.append("expiry_action", form.expiry_action);
        if (asDraft) fd.append("save_as_draft", "true");
        if (isMaster && form.reviewer_ids.length) fd.append("reviewer_ids", form.reviewer_ids.join(","));
        const urls = form.attachments.filter((a) => a.kind === "url").map((a) => a.url);
        const urlDescs = form.attachments.filter((a) => a.kind === "url").map((a) => a.description || "");
        if (urls.length) {
          fd.append("external_urls", urls.join("\n"));
          fd.append("external_url_descriptions", urlDescs.join("\n"));
        }
        for (const a of form.attachments) {
          if (a.kind === "file" && a.file) {
            fd.append("files", a.file, a.file.name);
            fd.append("file_descriptions", a.description || "");
          }
        }
        await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(asDraft ? "Saved as draft" : (form.is_important ? "Sent to Master Admin for approval" : "Submitted for approval"));
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const pickedUsers = useMemo(
    () => (form.target_user_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean),
    [form.target_user_ids, users],
  );
  const pickedReviewers = useMemo(
    () => (form.reviewer_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean),
    [form.reviewer_ids, users],
  );
  // Distinct roles present in the current directory — powers the
  // "Roles / Teams" quick-filter row at the top of the picker.
  const availableRoles = useMemo(() => {
    const order = [
      "master_admin", "hr_admin", "sales_admin", "sales_team",
      "accounts_admin", "dispatch_admin", "it_admin", "crm_manager",
      "documentation_team", "trainer", "employee",
    ];
    const present = new Set((users || []).map((u) => u.role).filter(Boolean));
    return order.filter((r) => present.has(r));
  }, [users]);

  const previewAtt = (a, idx) => {
    let icon, cls, label, meta;
    if (a.kind === "url") {
      icon = <LinkIcon size={14} />;
      cls = "bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400";
      label = a.url;
      meta = "External link";
    } else if (a.kind === "existing_file") {
      const flav = attachmentFlavor({ type: "file", filename: a.filename });
      icon = <flav.Icon size={14} />;
      cls = flav.cls;
      label = a.filename;
      meta = `${fmtSize(a.size)} · existing file`;
    } else {
      const flav = attachmentFlavor({ type: "file", filename: a.file?.name });
      icon = <flav.Icon size={14} />;
      cls = flav.cls;
      label = a.file?.name;
      meta = `${fmtSize(a.file?.size || 0)} · new upload`;
    }
    return (
      <div key={idx} className={`border rounded-md ${a.kind === "file" ? "border-indigo-200 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-900/20" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"}`}>
        <div className="flex items-center gap-2 px-3 py-2">
          <div className={`w-7 h-7 rounded-md flex items-center justify-center ${cls}`}>{icon}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold text-slate-800 dark:text-slate-200 truncate">{label}</div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400">{meta}</div>
          </div>
          <button type="button" onClick={() => removeAttachment(idx)} className="text-slate-400 dark:text-slate-500 hover:text-rose-500"><X size={14} /></button>
        </div>
        <div className="px-3 pb-2">
          <Input
            value={a.description || ""}
            onChange={(e) => updateAttachmentDescription(idx, e.target.value)}
            placeholder="Description for this attachment (optional)"
            className="h-8 text-[12px]"
            data-testid={`doc-form-attachment-desc-${idx}`}
          />
        </div>
      </div>
    );
  };

  const anyPickerOpen = pickerOpen || reviewerPickerOpen;

  return (
    <>
      <Dialog open={open && !anyPickerOpen} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="doc-form-dialog">
          {/* White banner with a subtle sky-blue accent — the Documents
              module colour. Simple, clean, and distinct from the violet
              Announcements module. */}
          <div className="relative bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4 rounded-t-lg">
            <div className="absolute inset-x-0 top-0 h-1 rounded-t-lg bg-sky-500" />
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-sky-50 dark:bg-sky-950/40 ring-1 ring-sky-200 dark:ring-sky-800 flex items-center justify-center text-sky-600 dark:text-sky-300">
                <Folder size={22} />
              </div>
              <div>
                <DialogTitle className="heading text-lg font-extrabold text-slate-900 dark:text-slate-100">
                  {isEditing ? (resubmitMode ? "Re-Edit & Resubmit" : "Edit Document") : "Add to Documents Corner"}
                </DialogTitle>
                <DialogDescription className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">
                  {isEditing ? "Update details, attachments (files & links), audience or schedule." : "Pick a type, write a description with dynamic mentions, attach files & links, then submit."}
                </DialogDescription>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-4">
            {/* Uniform "section card" layout — every field lives inside an
                identical framed block with a numbered chip + section label
                so the form reads top-to-bottom without visual dissonance.  */}

            {/* 01 — TYPE OF DOC */}
            <SectionCard step="01" title="Type of Document" required Icon={Sparkles}>
              <div className="flex flex-wrap gap-2" data-testid="doc-form-type-chips">
                {TAG_ORDER.map((t) => {
                  const active = form.tag === t;
                  const meta = TAG_META[t];
                  const Icon = TAG_ICONS[t] || FileText;
                  return (
                    <button
                      key={t} type="button"
                      onClick={() => setForm({ ...form, tag: t })}
                      className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border text-[12px] font-bold transition-all ${
                        active
                          ? "bg-sky-600 text-white border-sky-700 shadow-sm hover:bg-sky-700"
                          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                      }`}
                      data-testid={`doc-form-type-${t}`}
                    >
                      <Icon size={13} /> {meta.label}
                    </button>
                  );
                })}
              </div>
              {form.tag === "other" && (
                <Input className="mt-3" placeholder="Write your custom tag (e.g. Research Note)" value={form.tag_custom} onChange={(e) => setForm({ ...form, tag_custom: e.target.value })} data-testid="doc-form-tag-custom" maxLength={40} />
              )}
            </SectionCard>

            {/* 02 — PRIORITY */}
            <SectionCard step="02" title="Priority" Icon={ShieldAlert}>
              <div className="flex flex-wrap gap-2" data-testid="doc-form-priority-chips">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, is_important: true })}
                  className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border text-[12px] font-bold transition-all ${
                    form.is_important
                      ? "bg-rose-600 text-white border-rose-600 shadow-sm"
                      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-rose-300 hover:text-rose-700"
                  }`}
                  data-testid="doc-form-important-yes"
                >
                  <ShieldAlert size={13} /> Important
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, is_important: false })}
                  className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border text-[12px] font-bold transition-all ${
                    !form.is_important
                      ? "bg-slate-700 text-white border-slate-700 shadow-sm"
                      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-400"
                  }`}
                  data-testid="doc-form-important-no"
                >
                  Normal
                </button>
                <span className="text-[11px] text-slate-500 dark:text-slate-400 self-center">
                  {form.is_important
                    ? "Important docs skip the admin stage and go straight to Master Admin — no auto-approve."
                    : "Normal docs go through the standard admin approval flow."}
                </span>
              </div>
            </SectionCard>

            {/* 03 — TITLE */}
            <SectionCard step="03" title="Title" required Icon={FileText}>
              <Input placeholder="A short, descriptive title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-form-title" className="h-10" />
            </SectionCard>

            {/* 04 — DESCRIPTION */}
            <SectionCard
              step="04"
              title="Description"
              Icon={FileText}
              right={
                <div className="flex items-center gap-1 relative">
                  <button type="button" onClick={() => setPop(pop === "link" ? "" : "link")} className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 text-sky-700 dark:text-sky-300 bg-white dark:bg-slate-900 hover:bg-sky-50 dark:hover:bg-sky-900/30 text-[11px] font-semibold" data-testid="doc-form-insert-link"><LinkIcon size={11} /> Link</button>
                  <button type="button" onClick={() => setPop(pop === "contact" ? "" : "contact")} className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 text-indigo-700 dark:text-indigo-300 bg-white dark:bg-slate-900 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-[11px] font-semibold" data-testid="doc-form-insert-contact"><AtSign size={11} /> Contact</button>
                  <button type="button" onClick={() => setPop(pop === "doc" ? "" : "doc")} className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 text-violet-700 dark:text-violet-300 bg-white dark:bg-slate-900 hover:bg-violet-50 dark:hover:bg-violet-900/30 text-[11px] font-semibold" data-testid="doc-form-insert-doc"><Hash size={11} /> Doc</button>
                  <button type="button" onClick={() => setPop(pop === "ph" ? "" : "ph")} className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-slate-200 dark:border-slate-700 text-amber-700 dark:text-amber-300 bg-white dark:bg-slate-900 hover:bg-amber-50 dark:hover:bg-amber-900/30 text-[11px] font-semibold" data-testid="doc-form-insert-placeholder"><Braces size={11} /> Placeholder</button>
                  <button type="button" onClick={() => setShowPreview((s) => !s)} className={`inline-flex items-center gap-1 h-8 px-2 rounded-md border text-[11px] font-semibold ${showPreview ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-900 hover:bg-slate-50"}`} data-testid="doc-form-preview-toggle"><Eye size={11} /> {showPreview ? "Editing" : "Preview"}</button>
                  <InsertLinkPop open={pop === "link"} onClose={() => setPop("")} onInsert={insertToDescription} />
                  <InsertContactPop open={pop === "contact"} onClose={() => setPop("")} users={users} onInsert={insertToDescription} />
                  <InsertDocPop open={pop === "doc"} onClose={() => setPop("")} onInsert={insertToDescription} />
                  <InsertPlaceholderPop open={pop === "ph"} onClose={() => setPop("")} onInsert={insertToDescription} />
                </div>
              }
            >
              {!showPreview ? (
                <Textarea ref={descRef} rows={5} className="font-mono text-[13px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe this document. Use the buttons above to insert links, contacts, doc references or dynamic placeholders." data-testid="doc-form-desc" />
              ) : (
                <div className="min-h-[120px] px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-950" data-testid="doc-form-desc-preview">
                  <DescriptionPreview text={form.description} />
                </div>
              )}
              <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-1.5">
                Tokens: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">[Label](url)</code> · <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">@[Name]</code> · <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">#[Doc]</code> · <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{"{{placeholder}}"}</code>
              </div>
            </SectionCard>

            {/* 05 — AUDIENCE */}
            <SectionCard step="05" title="Audience" required Icon={Users}>
              <div className="flex flex-wrap gap-2" data-testid="doc-form-audience-chips">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, target_all: true })}
                  className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border text-[12px] font-bold transition-all ${
                    form.target_all
                      ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-emerald-300 hover:text-emerald-700"
                  }`}
                  data-testid="doc-form-audience-all"
                >
                  <Globe2 size={13} /> All Employees
                </button>
                <button
                  type="button"
                  onClick={() => { setForm({ ...form, target_all: false }); setPickerOpen(true); }}
                  className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border text-[12px] font-bold transition-all ${
                    !form.target_all
                      ? "bg-sky-600 text-white border-sky-600 shadow-sm"
                      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-sky-300 hover:text-sky-700"
                  }`}
                  data-testid="doc-form-audience-specific"
                >
                  <UserCheck size={13} /> Specific Users
                  {!form.target_all && form.target_user_ids.length > 0 && (
                    <span className="ml-1 bg-white/25 text-white px-1.5 py-0.5 rounded-full text-[10px]">{form.target_user_ids.length}</span>
                  )}
                </button>
              </div>
              {!form.target_all && pickedUsers.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {pickedUsers.slice(0, 12).map((u) => (
                    <span key={u.id} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-800 px-2 py-0.5 text-[11px]"><Users className="w-3 h-3" /> {u.name}</span>
                  ))}
                  {pickedUsers.length > 12 && <span className="text-[11px] text-slate-500 dark:text-slate-400 self-center">+{pickedUsers.length - 12} more</span>}
                </div>
              )}
            </SectionCard>

            {/* 06 — FILES & LINKS */}
            <SectionCard step="06" title="Files & Links" required Icon={LinkIcon} count={form.attachments.length}>
              {form.attachments.length > 0 && (
                <div className="space-y-1.5 mb-2" data-testid="doc-form-attachments">
                  {form.attachments.map((a, i) => previewAtt(a, i))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-400 text-slate-700 dark:text-slate-300 hover:text-indigo-700 dark:hover:text-indigo-300 rounded-md cursor-pointer text-[12px] font-semibold" data-testid="doc-form-add-file">
                  <FileText size={13} /> Add File(s)
                  <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
                </label>
                <div className="flex-1 min-w-[220px] inline-flex items-center gap-1.5">
                  <Input type="url" placeholder="https://…" value={pendingUrl}
                    onChange={(e) => setPendingUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
                    className="h-9" data-testid="doc-form-url-input" />
                  <Button type="button" onClick={addUrl} className="h-9 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white" data-testid="doc-form-add-url">
                    <LinkIcon size={13} className="mr-1" /> Add
                  </Button>
                </div>
              </div>
              <div className="mt-1 text-[10.5px] text-slate-500 dark:text-slate-400">Max 100 MB per file. Add any mix of files and external URLs.</div>
            </SectionCard>

            {/* 07 — SCHEDULE & EXPIRY */}
            <SectionCard step="07" title="Schedule & Expiry" Icon={CalendarClock}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10.5px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                    <CalendarClock size={11} /> Schedule for
                  </Label>
                  <Input type="datetime-local" className="mt-1.5" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} data-testid="doc-form-schedule" />
                </div>
                <div>
                  <Label className="text-[10.5px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                    <Clock size={11} /> Expires on
                  </Label>
                  <Input type="datetime-local" className="mt-1.5" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} data-testid="doc-form-end" />
                  {form.end_at && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">On expiry:</Label>
                      <select className="h-8 border border-slate-200 dark:border-slate-700 rounded-md px-2 text-[11px] bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200" value={form.expiry_action}
                        onChange={(e) => setForm({ ...form, expiry_action: e.target.value })} data-testid="doc-form-expiry-action">
                        <option value="archive">Archive (move to Archived)</option>
                        <option value="revise">Revise (return to owner as Draft)</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-1 text-[10.5px] text-slate-500 dark:text-slate-400">Both fields are optional — leave blank to publish immediately without expiry.</div>
            </SectionCard>

            {/* 08 — REVIEWERS (MA only) */}
            {isMaster && (
              <SectionCard step="08" title="Reviewers" Icon={UserCheck} count={form.reviewer_ids.length}>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => setReviewerPickerOpen(true)}
                    className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border text-[12px] font-bold bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-violet-400 hover:text-violet-700"
                    data-testid="doc-form-pick-reviewers">
                    <UserCheck size={13} />
                    {form.reviewer_ids.length === 0 ? "Pick reviewers" : `${form.reviewer_ids.length} reviewer${form.reviewer_ids.length > 1 ? "s" : ""}`}
                  </button>
                </div>
                {pickedReviewers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {pickedReviewers.map((u) => (
                      <span key={u.id} className="inline-flex items-center gap-1 rounded-full bg-violet-50 dark:bg-violet-950/40 text-violet-800 dark:text-violet-200 border border-violet-200 dark:border-violet-800 px-2 py-0.5 text-[11px]">
                        <UserCheck className="w-3 h-3" /> {u.name}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-1 text-[10.5px] text-slate-500 dark:text-slate-400">
                  Every reviewer must click <strong>Approve — Good to Go</strong> before the doc reaches Master Admin approval. Reviewers can leave per-field feedback and send the doc back for revision.
                </div>
              </SectionCard>
            )}
          </div>

          <DialogFooter className="px-6 pb-5 gap-2 flex-wrap">
            <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            {!resubmitMode && (
              <Button variant="outline" onClick={() => submit("draft")} disabled={busy} className="border-slate-400" data-testid="doc-form-save-draft">
                <Archive size={12} className="mr-1.5" /> Save as Draft
              </Button>
            )}
            {resubmitMode ? (
              <Button onClick={() => submit("resubmit")} disabled={busy} className="!bg-sky-600 hover:!bg-sky-700 !text-white !shadow-sky-500/30 hover:!shadow-sky-600/40" data-testid="doc-form-submit">
                <RefreshCcw size={12} className="mr-1.5" /> {busy ? "Resubmitting…" : "Resubmit for Approval"}
              </Button>
            ) : (
              <Button onClick={() => submit("publish")} disabled={busy} className="!bg-sky-600 hover:!bg-sky-700 !text-white !shadow-sky-500/30 hover:!shadow-sky-600/40" data-testid="doc-form-submit">
                {busy ? "Saving…" : isEditing ? "Save Changes" : (form.scheduled_for ? "Schedule Submit" : (isMaster ? "Publish Now" : "Submit for Approval"))}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserPickerDialog
        open={pickerOpen} onClose={() => setPickerOpen(false)}
        onDone={(ids) => setForm((f) => ({ ...f, target_user_ids: ids }))}
        users={users} initialSelectedIds={form.target_user_ids}
        title="Select audience"
        roleOptions={availableRoles}
      />
      <UserPickerDialog
        open={reviewerPickerOpen} onClose={() => setReviewerPickerOpen(false)}
        onDone={(ids) => setForm((f) => ({ ...f, reviewer_ids: ids }))}
        users={users} initialSelectedIds={form.reviewer_ids}
        title="Appoint reviewers (all must approve)"
        roleOptions={availableRoles}
      />
    </>
  );
}
